'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, ButtonLink, NumberedPagination, StatusLabel } from '~/components/primitives';
import type { Quotation } from '@funkey/types/graphql/graphql';
import { maybeMap } from '@funkey/shared';
import { ZohoHashURL } from '@funkey/api/src/modules/zoho/utils';
import { ZohoLaravelAPI } from '@funkey/api/src/modules/zoho/laravelAPI';
import Avatar from '~/features/auth/user/Avatar';
import { DateTime } from '~/components/primitives/DateTime';
import { useI18n } from '~/features/i18n';
import Table from '~/components/primitives/Table';
import useNotifications from '~/features/notifications/useNotifications';
import { FileDownloadSDK } from '~/utils/files';
import { useVATRate, VATKindDisplay } from '~/features/portals/money';
import { Auth } from '~/modules/auth/client';
import { usePortalCtx } from '~/features/global/portal';
import Link from 'next/link';
export const BizzQuotationTable: React.FC<{
  /** @default false */
  hideResponsible?: boolean;
  showBothVAT?: boolean;
  quotations: Array<Pick<Quotation, 'id' | 'name' | 'status' | 'createdAt' | 'zohoID'> & {
    total: Pick<Quotation['total'], 'exclVAT' | 'inclVAT'>;
    deal: Pick<Quotation['deal'], 'id' | 'name' | 'quoteSignLink'> & {
      user?: null | Pick<NonNullable<Quotation['deal']['user']>, 'id' | 'initials' | 'fullName' | 'abbreviatedName' | 'bizzMeta'>;
    };
    media: Pick<Quotation['media'], 'path'>;
  }>;
}> = props => {
  const $ = _c(46);
  const i18n = useI18n();
  const vat = useVATRate();
  const notifications = useNotifications();
  const auth = Auth.useSession("required");
  const portal = usePortalCtx("BIZZ");
  let t0;
  if ($[0] !== auth || $[1] !== portal) {
    t0 = q => {
      if (portal.permissions.includes("BIZZ_VIEW_QUOTATIONS_OTHERS")) {
        return true;
      }
      return q.deal?.user?.id === auth.user.id;
    };
    $[0] = auth;
    $[1] = portal;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const quotations = props.quotations.filter(t0);
  const pagination = NumberedPagination.useState(quotations.length);
  const rows = quotations.slice(pagination.pageBeginIndex, pagination.pageEndIndex);
  const T0 = Table;
  const t1 = "bizz-quotation-table";
  let t2;
  if ($[3] !== i18n) {
    t2 = i18n._("Name");
    $[3] = i18n;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <Table.HeaderCell>{t2}</Table.HeaderCell>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== i18n) {
    t4 = i18n._("Status");
    $[7] = i18n;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <Table.HeaderCell>{t4}</Table.HeaderCell>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== i18n || $[12] !== props.hideResponsible) {
    t6 = props.hideResponsible !== true && <><Table.HeaderCell>{i18n._("Responsible")}</Table.HeaderCell><Table.HeaderCell>{i18n._("Department")}</Table.HeaderCell></>;
    $[11] = i18n;
    $[12] = props.hideResponsible;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== i18n || $[15] !== props.showBothVAT) {
    t7 = props.showBothVAT ? i18n._("Total Excl. VAT") : <>{i18n._("Total")} <VATKindDisplay /></>;
    $[14] = i18n;
    $[15] = props.showBothVAT;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <Table.HeaderCell>{t7}</Table.HeaderCell>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== i18n || $[20] !== props.showBothVAT) {
    t9 = props.showBothVAT && <Table.HeaderCell>{i18n._("Total Incl. VAT")}</Table.HeaderCell>;
    $[19] = i18n;
    $[20] = props.showBothVAT;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== i18n) {
    t10 = i18n._("Created");
    $[22] = i18n;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = <Table.HeaderCell>{t10}</Table.HeaderCell>;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t3 || $[28] !== t5 || $[29] !== t6 || $[30] !== t8 || $[31] !== t9) {
    t12 = <>{t3}{t5}{t6}{t8}{t9}{t11}</>;
    $[26] = t11;
    $[27] = t3;
    $[28] = t5;
    $[29] = t6;
    $[30] = t8;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const t13 = Table;
  const t14 = !!rows?.length ? rows.map(quotation => {
    const quoteSignLink = maybeMap(quotation.deal.quoteSignLink || null, ZohoHashURL.toNormalisedURL);
    quoteSignLink?.searchParams.set("id", quotation.zohoID);
    return <Table.Row key={quotation.id} title={i18n._("View quotation")} actions={<>{quotation.status === "Sent" && <ButtonLink variant="outline" target="_blank" disabled={!quotation.deal.quoteSignLink} href={quoteSignLink ? ZohoHashURL.toString(quoteSignLink) : "#"}>{i18n._("Sign")}</ButtonLink>}<Button iconBefore="download" type="button" variant="outline" onClick={async () => {
        const response = await ZohoLaravelAPI.getQuotePDF(quotation.zohoID);
        if (!response.ok) {
          console.error(response);
          return notifications.dispatchError({
            message: i18n._("Failed to download PDF")
          });
        }
        FileDownloadSDK.downloadBlob(response.blob, `Funkey_Quote_${quotation.name ?? quotation.deal.name}.pdf`);
      }}>{i18n._("Download")}</Button></>}><Table.Cell><Link href={`${portal.baseURLPath}/events/${quotation.deal.id}`} target="_blank" className="hover:text-accent-turqoise hover:underline">{quotation.name ?? quotation.deal.name}</Link></Table.Cell><Table.Cell><StatusLabel status={quotation.status === "Won" ? "positive" : quotation.status === "Sent" ? "passive" : "negative"}>{quotation.status}</StatusLabel></Table.Cell>{props.hideResponsible !== true && <><Table.Cell><span className="flex items-center gap-2">{quotation.deal.user?.initials && <Avatar size="6" label={quotation.deal.user.initials} color="yellow" />}{quotation.deal.user?.fullName ?? quotation.deal.user?.abbreviatedName}</span></Table.Cell><Table.Cell>{quotation.deal.user?.bizzMeta?.department}</Table.Cell></>}<Table.Cell>{props.showBothVAT ? quotation.total.exclVAT : vat.isInclusive ? quotation.total.inclVAT : quotation.total.exclVAT}</Table.Cell>{props.showBothVAT && <Table.Cell>{quotation.total.inclVAT}</Table.Cell>}<Table.Cell><DateTime format="date" date={new Date(quotation.createdAt)} /></Table.Cell></Table.Row>;
  }) : <Table.NoRows title={i18n._("No quotations found within your current date range. Please adjust your date range or clear it to see all your quotations.")} button={<Button variant="tertiary">{i18n._("Clear filters")}</Button>} />;
  let t15;
  if ($[33] !== t13.Body || $[34] !== t14) {
    t15 = <t13.Body>{t14}</t13.Body>;
    $[33] = t13.Body;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== T0 || $[37] !== t12 || $[38] !== t15) {
    t16 = <T0 data-testid={t1} head={t12}>{t15}</T0>;
    $[36] = T0;
    $[37] = t12;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== i18n || $[41] !== pagination) {
    t17 = pagination.totalPages > 0 && <NumberedPagination className="mt-5" {...pagination} nextText={i18n._("Next")} previousText={i18n._("Previous")} />;
    $[40] = i18n;
    $[41] = pagination;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t16 || $[44] !== t17) {
    t18 = <>{t16}{t17}</>;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  return t18;
};